import React from "react";
import ReactDOM from "react-dom";
import NumberFormat from "react-number-format";
import numeral from "numeral";

import moment from "moment";

import { Button, getRange } from "aible";

import ResponsiveChart from "components/RunPlot/ResponsiveChart";

import { AutoSizer } from "react-virtualized";

import {
  LineChart,
  Line,
  Scatter,
  CartesianGrid,
  ComposedChart,
  Area,
  CartesianAxis,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
  ReferenceDot,
  ReferenceArea,
  ResponsiveContainer,
  ErrorBar,
  Label,
  Cell,
  LabelList
} from "recharts";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import ModelTileContainer from "containers/Tiles/ModelTile";

import { LineInput } from "components/Inputs/LineInput";

import styles from "./style.module.css";

const CustomToolTip = (props) => {
  const { active, payload, label, setupLabels } = props;
  if (!active || !payload) {
    return null;
  }

  const renderRange = (rPayload) => {
    const values = rPayload && rPayload.value;
    if (!values || values.length !== 2) {
      return null;
    }

    return (
      <React.Fragment key={rPayload.dataKey}>
        <tr className={styles.tBody}>
          <td className={styles.tLabel} style={{ color: rPayload.color }}>
            {"Upper Bound: "}
          </td>
          <td>{numeral(values[1]).format("0.00")}</td>
        </tr>
        <tr className={styles.tBody}>
          <td className={styles.tLabel} style={{ color: rPayload.color }}>
            {"Lower Bound: "}
          </td>
          <td>{numeral(values[0]).format("0.00")}</td>
        </tr>
      </React.Fragment>
    );
  };

  const dPayload = payload.find((e) => e && e.payload && e.payload.Date);
  const date = dPayload && dPayload.payload && dPayload.payload.Date;

  return (
    <div className={styles.cTooltip}>
      <div className={styles.tHeader}>{date}</div>
      <table className={styles.tTable}>
        <tbody>
          {payload.map((p, i) => {
            if (!p || (p.payload && p.payload.hidePrediction && p.dataKey !== "Actual")) {
              return null;
            }
            if (p.dataKey === "Range") {
              // return renderRange(p);
              return null;
            }
            const label =
              p.dataKey === "Actual" ? setupLabels.numberPredicted : "Predicted " + setupLabels.numberPredicted;

            return (
              <tr key={p.dataKey}>
                <td className={styles.tLabel} style={{ color: p.color }}>
                  {label + ": "}
                </td>
                <td>{(setupLabels.unitType || "") + numeral(p.value).format("0.00")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CustomLabel = (props) => {
  const { value, position, offset, viewBox } = props;

  let x, y;
  switch (position) {
    case "top":
      x = viewBox.x + offset;
      y = 0 + offset;
      break;
    case "bottom":
      x = viewBox.x + viewBox.width / 2;
      y = viewBox.y + viewBox.height + offset;
      break;
    default:
      x = viewBox.x + offset;
      y = viewBox.y + offset;
  }

  return (
    <text textAnchor="start" className={styles.aLabel}>
      <tspan x={x} y={y}>
        {value}
      </tspan>
    </text>
  );
};

class PredictionChart extends React.PureComponent {
  renderLegend() {
    const {} = this.props;
    return (
      <div className={styles.forecastChartLegend}>
        <div className={styles.forecastChartLegendItem}>
          <div className={styles.forecastChartLine} />
          <div className={styles.forecastChartLabel}>{"Prediction"}</div>
        </div>
        <div className={styles.forecastChartLegendItem}>
          <div className={styles.forecastChartCircle} />
          <div className={styles.forecastChartLabel}>{"Likely Range"}</div>
        </div>
      </div>
    );
  }

  render() {
    const {
      models,
      model,
      forecastPredictionPlot,
      fetchForecast,
      forecastTime,
      updateForecastTime,
      forecastTimeOptions,
      useForecastLocalRatio,
      forecastTimeUnit,
      updateForecastTimeUnit,
      setupLabels,
      forecastTimeCol,
      forecastChartDateRange
    } = this.props;

    if (!forecastPredictionPlot) {
      return <React.Fragment />;
    }

    const retData = forecastPredictionPlot.slice(forecastChartDateRange[0], forecastChartDateRange[1] + 1);

    let yMin = Infinity;
    let yMax = -Infinity;

    let predictionRange = {
      min: undefined,
      max: undefined
    };
    retData.forEach((d, i) => {
      if (!isNaN(d.Actual)) {
        yMin = Math.min(yMin, d.Actual);
        yMax = Math.max(yMax, d.Actual);
      }
      if (!isNaN(d.Prediction)) {
        if (predictionRange.min === undefined) {
          predictionRange.min = i;
        }
        if (predictionRange.max === undefined) {
          predictionRange.max = i;
        } else {
          predictionRange.max = Math.max(i, predictionRange.max);
        }
        yMin = Math.min(yMin, d.Prediction);
        yMax = Math.max(yMax, d.Prediction);
      }
      if (d.Range) {
        yMin = Math.min(yMin, d.Range[0]);
        yMax = Math.max(yMax, d.Range[1]);
      }
    });

    const colorBreakPoints = {
      start: ((predictionRange.min || 0) / (retData.length - 1 || 1)) * 100,
      stop: ((predictionRange.max || 0) / (retData.length - 1 || 1)) * 100
    };

    const yDomain = getRange(yMin, yMax, true, 1);
    const roundValues = Math.abs(yMax - yMin) > 10;

    return (
      <div className={styles.forecastChart} id="forecastPlot">
        {this.renderLegend()}
        <AutoSizer>
          {({ width, height }) => (
            <ComposedChart
              data={retData}
              margin={{
                top: 30,
                right: 20,
                left: 20,
                bottom: 40
              }}
              syncId="forecastCharts"
              width={width}
              height={height}
            >
              <defs>
                <linearGradient id="rangeColor" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#D6CCFF00" />
                  <stop offset={colorBreakPoints.start + "%"} stopColor="#D6CCFF00" />
                  <stop offset={colorBreakPoints.start + "%"} stopColor="#D6CCFF" />
                  <stop offset={colorBreakPoints.stop + "%"} stopColor="#D6CCFF" />
                  <stop offset={colorBreakPoints.stop + "%"} stopColor="#D6CCFF00" />
                  <stop offset="100%" stopColor="#D6CCFF00" />
                </linearGradient>
              </defs>
              <XAxis dataKey="Date" name={"Date"}>
                <Label value={String(forecastTimeCol || "Date").toUpperCase()} offset={5} position="bottom" />
              </XAxis>
              <YAxis
                type="number"
                name={"Prediction"}
                domain={yDomain}
                tickFormatter={(tick) => {
                  const retTick = roundValues ? Math.round(tick) : tick;
                  return setupLabels.unitType + numeral(retTick).format("0.[00]a");
                }}
              >
                <Label value={String(setupLabels.numberPredicted).toUpperCase()} offset={15} position="top" />
              </YAxis>
              <CartesianGrid strokeDasharray="3" vertical={false} />
              <Area type="monotone" dataKey="Range" stroke="url(#rangeColor)" fill="url(#rangeColor)" />
              <Line dataKey="Actual" dot={false} stroke="#7E69D7" strokeWidth={3} />
              <Line
                type="monotone"
                dataKey="Prediction"
                dot={false}
                stroke="#7E69D7"
                strokeWidth={3}
                strokeDasharray="5 5"
              />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomToolTip setupLabels={setupLabels} />} />
            </ComposedChart>
          )}
        </AutoSizer>
      </div>
    );
  }
}

export default ResponsiveChart(PredictionChart);
